// frontend-react/src/components/ImageDescriptionModal.tsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Modal, Radio, Space, Spin, Typography, Upload } from 'antd';
import { InboxOutlined, DownloadOutlined, ReloadOutlined, FileOutlined } from '@ant-design/icons';
import {
  downloadImageDescription,
  pollImageDescriptionStatus,
  submitImageDescription,
} from '../services/imageDescriptionService';

const { Dragger } = Upload;
const { Text } = Typography;

type Step = 'idle' | 'processing' | 'done' | 'error';
type Placement = 'after_image' | 'appendix' | 'caption_only';

interface Props {
  visible: boolean;
  onClose: () => void;
}

const ImageDescriptionModal: React.FC<Props> = ({ visible, onClose }) => {
  const [step, setStep] = useState<Step>('idle');
  const [file, setFile] = useState<File | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [downloading, setDownloading] = useState(false);
  const [placement, setPlacement] = useState<Placement>('caption_only');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    stopPolling();
    setStep('idle');
    setFile(null);
    setTaskId(null);
    setErrorMsg('');
    setDownloading(false);
    setPlacement('caption_only');
  }, [stopPolling]);

  // 開始輪詢
  useEffect(() => {
    if (step !== 'processing' || !taskId) return;

    pollRef.current = setInterval(async () => {
      try {
        const res = await pollImageDescriptionStatus(taskId);
        if (res.status === 'done') {
          stopPolling();
          setStep('done');
        } else if (res.status === 'error') {
          stopPolling();
          setErrorMsg(res.error || '圖片分析失敗，請稍後再試');
          setStep('error');
        }
      } catch {
        stopPolling();
        setErrorMsg('無法取得任務狀態，請稍後再試');
        setStep('error');
      }
    }, 2000);

    return stopPolling;
  }, [step, taskId, stopPolling]);

  const handleStart = useCallback(async () => {
    if (!file) return;
    try {
      setStep('processing');
      const { task_id } = await submitImageDescription(file, placement);
      setTaskId(task_id);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '上傳失敗，請稍後再試';
      setErrorMsg(msg);
      setStep('error');
    }
  }, [file, placement]);

  const handleDownload = useCallback(async () => {
    if (!taskId) return;
    setDownloading(true);
    try {
      await downloadImageDescription(taskId, file?.name ?? 'document');
    } catch {
      setErrorMsg('下載失敗，請稍後再試');
      setStep('error');
    } finally {
      setDownloading(false);
    }
  }, [taskId, file]);

  const handleClose = useCallback(() => {
    if (step === 'processing') return;
    onClose();
  }, [step, onClose]);

  // ── 各步驟畫面 ──────────────────────────────────────────────────

  const renderIdle = () => (
    <Space direction='vertical' style={{ width: '100%' }} size='large'>
      {!file ? (
        <Dragger
          multiple={false}
          accept='.docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          maxCount={1}
          beforeUpload={f => {
            setFile(f);
            return false; // 不自動上傳
          }}
          fileList={[]}
        >
          <p className='ant-upload-drag-icon'>
            <InboxOutlined style={{ fontSize: '40px', color: '#4a7c59' }} />
          </p>
          <p className='ant-upload-text'>上傳文件以提取圖片文字</p>
          <p className='ant-upload-hint'>支援 Word（.docx）與 PDF 檔案，上限 10 MB</p>
        </Dragger>
      ) : (
        <div
          style={{
            padding: '16px 20px',
            background: '#f6ffed',
            border: '1px solid #b7eb8f',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <FileOutlined style={{ fontSize: '20px', color: '#52c41a' }} />
          <Text style={{ flex: 1 }}>{file.name}</Text>
        </div>
      )}
      <div>
        <Text type='secondary' style={{ fontSize: '13px' }}>
          OCR 文字放置方式
        </Text>
        <Radio.Group
          value={placement}
          onChange={e => setPlacement(e.target.value)}
          style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}
        >
          <Radio value='caption_only'>
            <span>只加圖號（建議）</span>
            <br />
            <Text type='secondary' style={{ fontSize: '12px' }}>
              每張圖片下方加上「圖N：說明」，最快速、不改變排版，適合審閱用途
            </Text>
          </Radio>
          <Radio value='appendix'>
            <span>集中到文末附錄</span>
            <br />
            <Text type='secondary' style={{ fontSize: '12px' }}>
              原始排版完全保留，OCR 文字集中在文末，適合型錄、多圖混排
            </Text>
          </Radio>
          <Radio value='after_image'>
            <span>圖片後方插入完整文字</span>
            <br />
            <Text type='secondary' style={{ fontSize: '12px' }}>
              完整 OCR 文字緊接在每張圖片下方，適合圖片較少的文件
            </Text>
          </Radio>
        </Radio.Group>
      </div>
      <div style={{ textAlign: 'right' }}>
        <Space>
          {file ? (
            <Button onClick={() => setFile(null)} icon={<ReloadOutlined />}>
              重新上傳
            </Button>
          ) : (
            <Button onClick={handleClose}>取消</Button>
          )}
          <Button
            type='primary'
            disabled={!file}
            onClick={handleStart}
            style={{ background: '#4a7c59', borderColor: '#4a7c59' }}
          >
            開始轉換
          </Button>
        </Space>
      </div>
    </Space>
  );

  const renderProcessing = () => (
    <div style={{ textAlign: 'center', padding: '32px 0' }}>
      <Spin size='large' />
      <div style={{ marginTop: '16px' }}>
        <Text>圖片轉換中，請耐心等候...</Text>
      </div>
    </div>
  );

  const renderDone = () => (
    <Space direction='vertical' style={{ width: '100%' }} size='large'>
      <Alert
        type='success'
        message='轉換完成'
        description='已將圖片文字轉換完成，點擊下方 Word 檔下載。'
        showIcon
      />
      <div style={{ textAlign: 'center' }}>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={reset}>
            重新上傳
          </Button>
          <Button
            type='primary'
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            loading={downloading}
            style={{ background: '#4a7c59', borderColor: '#4a7c59' }}
          >
            下載
          </Button>
        </Space>
      </div>
    </Space>
  );

  const renderError = () => (
    <Space direction='vertical' style={{ width: '100%' }} size='large'>
      <Alert type='error' message='發生錯誤' description={errorMsg} showIcon />
      <div style={{ textAlign: 'right' }}>
        <Space>
          <Button onClick={handleClose}>關閉</Button>
          <Button onClick={reset}>重試</Button>
        </Space>
      </div>
    </Space>
  );

  const bodyMap: Record<Step, () => React.ReactNode> = {
    idle: renderIdle,
    processing: renderProcessing,
    done: renderDone,
    error: renderError,
  };

  return (
    <Modal
      title='圖片轉文字'
      open={visible}
      onCancel={handleClose}
      footer={null}
      closable={step !== 'processing'}
      maskClosable={step !== 'processing'}
      width={520}
      destroyOnClose
      afterClose={reset}
    >
      {bodyMap[step]()}
    </Modal>
  );
};

export default ImageDescriptionModal;
